import { BarItemProps, BarLayer, ComputedDatum, ResponsiveBar } from "@nivo/bar";
import { hexColorRegex } from "validations/matchers";
import { BarsChartData } from "pages/Corporate/components/BarChart/BarsChartInterfaces";
import { Typography, useTheme } from "@mui/material";
import { ReportDowntimeBarChartTrendLayer } from "./ReportDowntimeBarChartTrendLayer.view";
import { ReportDowntimeBarChartPercentOnFirstBarLayer } from "./ReportDowntimeBarChartPercentOnFirstBarLayer";
import { ReportDowntimeBarChartPercentOnTotalBarLayer } from "./ReportDowntimeBarChartPercentOnTotalBarLayer";
import { ReportDowntimeBarChartSummaryOnFirstBarLayer } from "./ReportDowntimeBarChartSummaryOnFirstBarLayer";
import { ReportDowntimeBarChartSummaryOnTotalBarLayer } from "./ReportDowntimeBarChartSummaryOnTotalBarLayer";
import { pSBC, truncateLongText } from "utilities/utilities";
import { FC } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useStyles } from "./ReportDowntimeBarChart.styles";

type TDowntimeBarChart = BarsChartData & {
    showPercentLayerOnTotalBar?: boolean;
    noPercent?: boolean;
    noTrend?: boolean;
    noLegend?: boolean;
    onBarClick?: (value: { id: string | number; name: string | number; color: string }) => void;
    selectedBar?: string | number;
    customBarComponent?: FC<
        BarItemProps<{
            [key: string]: string | number;
        }>
    >;
};

const ReportDowntimeBarChart = ({
    data,
    index,
    showPercentLayerOnTotalBar,
    noPercent,
    noTrend,
    noLegend,
    customBarComponent,
    selectedBar,
    onBarClick,
}: TDowntimeBarChart) => {
    const classes = useStyles();
    const themeStyles = useTheme();

    const theme = {
        axis: {
            fontSize: "12px",
            ticks: {
                text: {
                    fontSize: "10px",
                    fontFamily: "Open Sans",
                    fill: themeStyles.palette.text.primary,
                },
            },
        },
        tooltip: {
            container: {
                display: "none",
            },
        },
        legends: {
            text: {
                fontSize: 8,
                fill: themeStyles.palette.text.primary,
                outlineWidth: 0,
                outlineColor: "transparent",
                transform: "rotate(-20deg)",
            },
        },
    };

    const colors: string[] = [];
    const keys: string[] = [];

    data.forEach((dataItem) => {
        return Object.values(dataItem).forEach((value) => {
            if (typeof value === "string" && hexColorRegex.test(value) && !colors.includes(value)) {
                colors.push(value);
            }
        });
    });

    data.forEach((dataItem) => {
        Object.keys(dataItem).forEach((key) => {
            if (
                key !== "trend" &&
                key !== "countryCode" &&
                key !== "trendValue" &&
                key !== "totalDowntimeByMill" &&
                key !== `${index}Id` &&
                key !== index &&
                key !== "numberId" &&
                !keys.includes(key) &&
                !key.includes("Color")
            ) {
                keys.push(key);
            }
        });
    });
    const handleBarClick = (datum: ComputedDatum<{ [key: string]: string | number }> & { color: string }) => {
        const parsedDatum = {
            id: datum.data[`${index}Id`],
            name: datum.data[index],
            color: datum.color,
        };

        if (onBarClick) onBarClick(parsedDatum);
    };

    const colorMapping: Record<string, string> = data.reduce((acc: Record<string, string>, item) => {
        if (!selectedBar) {
            acc[item[index]] = colors[0];
            return acc;
        } else if (selectedBar === item[index]) {
            acc[item[index]] = colors[0];
            return acc;
        } else {
            acc[item[index]] = pSBC(0.4, colors[0]) || colors[0];
            return acc;
        }
    }, {} as Record<string, string>);

    const TrendLayer = ReportDowntimeBarChartTrendLayer as unknown as BarLayer<{
        [key: string]: string | number;
    }>;
    const PercentOnTotalBarLayer = (noPercent
        ? ReportDowntimeBarChartSummaryOnTotalBarLayer
        : ReportDowntimeBarChartPercentOnTotalBarLayer) as unknown as BarLayer<{
        [key: string]: string | number;
    }>;
    const PercentOnFirstBarLayer = (noPercent
        ? ReportDowntimeBarChartSummaryOnFirstBarLayer
        : ReportDowntimeBarChartPercentOnFirstBarLayer) as unknown as BarLayer<{
        [key: string]: string | number;
    }>;

    const extraLayers = showPercentLayerOnTotalBar
        ? [...(noTrend ? [] : [TrendLayer]), PercentOnFirstBarLayer]
        : [PercentOnTotalBarLayer, ...(noTrend ? [] : [TrendLayer])];

    return (
        <ResponsiveBar
            data={data}
            keys={keys}
            indexBy={`${index}Id`}
            enableGridX
            theme={theme}
            onClick={onBarClick ? handleBarClick : undefined}
            enableGridY={false}
            margin={{ top: 0, right: noTrend ? 50 : 80, bottom: noLegend ? 0 : 25, left: 70 }}
            layout="horizontal"
            enableLabel={false}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: true }}
            borderColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            barComponent={customBarComponent}
            axisLeft={{
                format: (value) => {
                    const dataItem = data.find((d) => d[`${index}Id`] === value);
                    return truncateLongText(dataItem ? dataItem[index]?.toString() : "", 8);
                },
                renderTick(props) {
                    const { x, y, value } = props;
                    const dataItem = data.find((d) => d[`${index}Id`] === value);
                    const displayName = dataItem ? dataItem[index]?.toString() : "";

                    return (
                        <Tooltip
                            title={<Typography className={classes.tooltipTexts}>{displayName}</Typography>}
                            placement="left"
                            PopperProps={{
                                className: classes.tooltip,
                            }}
                        >
                            <g transform={`translate(${x},${y})`}>
                                <text
                                    x={-2}
                                    y={0}
                                    textAnchor="end"
                                    dominantBaseline="central"
                                    fill={themeStyles.palette.text.secondary}
                                    className={classes.text}
                                >
                                    <>{truncateLongText(displayName, 8)}</>
                                </text>
                            </g>
                        </Tooltip>
                    );
                },
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "",
                legendPosition: "middle",
                legendOffset: -40,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
            }}
            legends={
                noLegend
                    ? []
                    : [
                          {
                              anchor: "bottom",
                              dataFrom: "keys",
                              direction: "row",
                              itemHeight: 0,
                              itemWidth: 30,
                              justify: false,
                              itemsSpacing: 15,
                              symbolSpacing: 3,
                              symbolSize: 5,
                              translateX: -20,
                              translateY: 20,
                          },
                      ]
            }
            role="application"
            padding={0.4}
            colors={onBarClick ? (e) => colorMapping[e.data[index]] : colors}
            layers={["grid", "axes", "bars", "markers", "legends", ...extraLayers]}
            innerPadding={1}
        />
    );
};

export default ReportDowntimeBarChart;
